/* IMPERIUM photo-review wall — see sections/imperium-reviews.liquid. */
(() => {
  if (customElements.get('imp-review-wall')) return;
  const API = 'https://pub.kudosi.ai/storefront/reviews';
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  // TikTok-CDN photos come as 300x300 crops; the crop size is a URL parameter, the original is "origin-jpeg".
  const sized = (u, w, h) => (/ttcdn/.test(u) ? u.replace(/crop-webp:\d+:\d+/, `crop-webp:${w}:${h}`) : u);
  const full = (u) => (/ttcdn/.test(u) ? u.replace(/tplv-([a-z0-9]+)-crop-webp:\d+:\d+\.jpeg/, 'tplv-$1-origin-jpeg.jpeg') : u);
  const stars = (n) => '★★★★★'.slice(0, n) + '<span class="imp-rw__star-off">' + '★★★★★'.slice(n) + '</span>';

  class ReviewWall extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-imp-rw-dialog]');
      this.querySelector('[data-imp-rw-close]').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.dialog.close(); });
      const io = new IntersectionObserver((en) => { if (en[0].isIntersecting) { io.disconnect(); this.load(); } }, { rootMargin: '600px' });
      io.observe(this);
    }

    async load() {
      const shop = this.dataset.shop, min = +this.dataset.minStar || 4, max = +this.dataset.max || 60;
      const reviews = [], products = {};
      let after = '', total = 0;
      try {
        for (let page = 0; page < 12 && reviews.length < max; page++) {
          const q = `?shop_domain=${encodeURIComponent(shop)}&limit=15&widget_type=carousel&name_format=first_name&need_media=true${after ? '&after_review_id=' + after : ''}`;
          const d = await (await fetch(API + q)).json();
          if (!page) total = d.total_reviews || 0;
          (d.products || []).forEach((p) => { products[p.product_id] = p; });
          for (const r of d.reviews || []) {
            const photos = (r.media_list || []).filter((m) => m.type === 'photo' && /^https?:/.test(m.media_url));
            const p = products[r.product_id];
            if (!photos.length || r.star < min || (p && /^\s*free returns/i.test(p.title))) continue;
            reviews.push({ ...r, photos, product: p });
          }
          if (!d.has_more || !d.reviews || !d.reviews.length) break;
          after = d.reviews[d.reviews.length - 1].id;
        }
      } catch (e) { /* fall through: hide if nothing loaded */ }
      if (reviews.length < 6) { this.closest('.shopify-section')?.setAttribute('hidden', ''); return; }
      this.reviews = reviews.slice(0, max);
      const meta = this.querySelector('[data-imp-rw-meta]');
      if (total) { this.querySelector('[data-imp-rw-count]').textContent = total.toLocaleString() + '+'; meta.hidden = false; }
      this.render();
    }

    card(r, i) {
      const text = (r.is_translated && r.content_translated) || r.content || '';
      const prod = r.product ? r.product.title.replace(/\s*[-–|].*$/, '') : '';
      return `<button type="button" class="imp-rw__card" data-i="${i}" aria-label="${esc(r.author)}, ${r.star} stars${prod ? ', ' + esc(prod) : ''}">
        <img src="${esc(sized(r.photos[0].media_url, 480, 600))}" alt="" loading="lazy" decoding="async" onerror="this.closest('.imp-rw__card').remove()">
        ${r.photos.length > 1 ? `<span class="imp-rw__more">+${r.photos.length - 1}</span>` : ''}
        <span class="imp-rw__overlay">
          <span class="imp-rw__card-stars">${stars(r.star)}</span>
          ${text ? `<span class="imp-rw__quote">“${esc(text.trim())}”</span>` : ''}
          <span class="imp-rw__who">${esc(r.author)}${prod ? ` <span>· ${esc(prod)}</span>` : ''}</span>
        </span>
      </button>`;
    }

    render() {
      const half = Math.ceil(this.reviews.length / 2);
      const rows = [this.reviews.slice(0, half), this.reviews.slice(half)];
      const wrap = this.querySelector('[data-imp-rw-rows]');
      wrap.innerHTML = rows.map((list, ri) => {
        const cards = list.map((r) => this.card(r, this.reviews.indexOf(r))).join('');
        // the track holds the list twice so the drift loops seamlessly (animates to -50%)
        return `<div class="imp-rw__row imp-rw__row--${ri ? 'rev' : 'fwd'}" style="--imp-rw-dur:${Math.max(40, list.length * 6)}s"><div class="imp-rw__track">${cards}<span class="imp-rw__dupe" aria-hidden="true">${cards}</span></div></div>`;
      }).join('');
      wrap.querySelectorAll('.imp-rw__dupe .imp-rw__card').forEach((b) => b.setAttribute('tabindex', '-1'));
      wrap.addEventListener('click', (e) => { const b = e.target.closest('.imp-rw__card'); if (b) this.open(+b.dataset.i); });
      // touch: hold pauses the drift
      wrap.querySelectorAll('.imp-rw__row').forEach((row) => {
        row.addEventListener('touchstart', () => row.classList.add('is-paused'), { passive: true });
        row.addEventListener('touchend', () => setTimeout(() => row.classList.remove('is-paused'), 1500), { passive: true });
      });
      this.classList.add('is-ready');
    }

    open(i) {
      const r = this.reviews[i]; if (!r) return;
      const text = (r.is_translated && r.content_translated) || r.content || '';
      const p = r.product;
      const date = r.created_at ? new Date(r.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '';
      const box = this.querySelector('[data-imp-rw-detail]');
      box.innerHTML = `
        <div class="imp-rw__gallery"><img class="imp-rw__main" src="${esc(full(r.photos[0].media_url))}" alt="Photo from ${esc(r.author)}'s review"></div>
        ${r.photos.length > 1 ? `<div class="imp-rw__thumbs">${r.photos.map((m, k) => `<button type="button" class="${k ? '' : 'is-active'}" data-src="${esc(full(m.media_url))}"><img src="${esc(sized(m.media_url, 160, 160))}" alt=""></button>`).join('')}</div>` : ''}
        <div class="imp-rw__body">
          <div class="imp-rw__detail-stars">${stars(r.star)}</div>
          <p class="imp-rw__detail-who"><strong>${esc(r.author)}</strong>${date ? ` · ${esc(date)}` : ''}</p>
          ${text ? `<p class="imp-rw__detail-text">${esc(text.trim()).replace(/\n+/g, '<br>')}</p>` : ''}
          ${p ? `<a class="imp-rw__product" href="/products/${esc(p.handle)}">${p.image ? `<img src="${esc(p.image)}" alt="" width="56" height="56">` : ''}<span><small>Reviewed item</small>${esc(p.title)}</span><span class="imp-rw__shop">Shop →</span></a>` : ''}
        </div>`;
      box.querySelectorAll('.imp-rw__thumbs button').forEach((b) => b.addEventListener('click', () => {
        box.querySelector('.imp-rw__main').src = b.dataset.src;
        box.querySelectorAll('.imp-rw__thumbs button').forEach((x) => x.classList.toggle('is-active', x === b));
      }));
      this.dialog.showModal();
    }
  }
  customElements.define('imp-review-wall', ReviewWall);
})();
